import React, { useState } from "react";
import TitleStripe from "../../common/TitleStripe";
import { HiCheckCircle, HiExclamationCircle } from "react-icons/hi";
import { FaBuilding, FaMapMarkerAlt, FaRegCalendarCheck } from "react-icons/fa";

function formatDate(value) {
  if (!value) return value;
  return String(value).split(" ")[0];
}

function formatEndereco(endereco = {}) {
  const { logradouro, numero, complemento, bairro, cidade, uf, cep } =
    endereco || {};
  const linha1 = [logradouro, numero].filter(Boolean).join(", ");
  const linha2 = [complemento, bairro].filter(Boolean).join(" - ");
  const linha3 = [cidade && uf ? `${cidade}/${uf}` : cidade || uf, cep]
    .filter(Boolean)
    .join(" - ");
  return { linha1, linha2, linha3 };
}

const HISTORICO_LIMIT = 5;

export default function FGTSQuery({ data }) {
  const [showAllHistorico, setShowAllHistorico] = useState(false);

  const fgts = data?.fgts || data;

  if (!fgts) return null;

  const irregular = Boolean(fgts?.possuiIrregularidade);
  const endereco = formatEndereco(fgts?.endereco);
  const historico = Array.isArray(fgts?.historico) ? fgts.historico : [];
  const historicoVisivel = showAllHistorico
    ? historico
    : historico.slice(0, HISTORICO_LIMIT);

  return (
    <section className="mt-6" id="fgts">
      <TitleStripe>FGTS | Certidão Regularidade do FGTS</TitleStripe>

      <div
        className={`mt-4 overflow-hidden rounded-2xl border shadow-sm ${
          irregular
            ? "border-red-200 bg-red-50"
            : "border-emerald-200 bg-emerald-50"
        }`}
      >
        <div className="flex items-start gap-4 px-5 py-4">
          <span
            className={`mt-0.5 flex h-12 w-12 shrink-0 items-center justify-center rounded-full ring-1 ${
              irregular
                ? "bg-red-100 text-red-600 ring-red-200"
                : "bg-emerald-100 text-emerald-600 ring-emerald-200"
            }`}
          >
            {irregular ? (
              <HiExclamationCircle className="h-7 w-7" />
            ) : (
              <HiCheckCircle className="h-7 w-7" />
            )}
          </span>
          <div className="min-w-0 flex-1">
            <p
              className={`text-base font-semibold ${
                irregular ? "text-red-900" : "text-emerald-900"
              }`}
            >
              {irregular ? "Empresa Irregular" : "Empresa Regular"}
            </p>
            <p
              className={`mt-0.5 text-sm leading-snug ${
                irregular ? "text-red-800" : "text-emerald-800"
              }`}
            >
              {fgts?.status || "-"}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-3 grid gap-3 grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
          <p className="text-[11px] text-gray-500">Razão social</p>
          <p className="text-sm font-semibold text-gray-900">
            {fgts?.razaoSocial || "-"}
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
          <p className="text-[11px] text-gray-500">Número do certificado</p>
          <p className="text-sm font-semibold text-gray-900">
            {fgts?.numeroCertificado || "-"}
          </p>
        </div>
      </div>

      <div className="mt-3 grid gap-3 grid-cols-2">
        <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2">
          <FaMapMarkerAlt className="h-3.5 w-3.5 shrink-0 text-gray-400" />
          <p className="truncate text-sm text-gray-800">
            {[endereco.linha1, endereco.linha2, endereco.linha3]
              .filter(Boolean)
              .join(" - ") || "-"}
          </p>
        </div>

        <div className="flex items-center gap-2 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2">
          <FaRegCalendarCheck className="h-3.5 w-3.5 shrink-0 text-blue-600" />
          <p className="text-sm text-blue-900">
            <span className="font-semibold">Validade:</span>{" "}
            {formatDate(fgts?.periodoValidade?.inicio) || "-"} até{" "}
            {formatDate(fgts?.periodoValidade?.fim) || "-"}
          </p>
        </div>
      </div>

      {historico.length > 0 && (
        <div className="mt-4 overflow-hidden rounded-xl border border-gray-200">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-4 py-2">
            <FaBuilding className="h-3.5 w-3.5 text-gray-400" />
            <p className="text-sm font-semibold text-gray-700">
              Histórico de certificados
            </p>
            <span className="ml-auto rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[10px] font-medium text-gray-600">
              {historico.length}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-white">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Data de emissão
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Número CRF
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Validade
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {historicoVisivel.map((item, idx) => (
                  <tr key={`${item?.numeroCrf || "crf"}-${idx}`}>
                    <td className="px-4 py-2 text-sm font-medium text-gray-900">
                      {formatDate(item?.dataEmissao) || "-"}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {item?.numeroCrf || "-"}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {formatDate(item?.periodoValidade?.inicio) || "-"} até{" "}
                      {formatDate(item?.periodoValidade?.fim) || "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {historico.length > HISTORICO_LIMIT && (
            <div className="flex justify-center border-t border-gray-200 bg-white py-2">
              <button
                type="button"
                onClick={() => setShowAllHistorico((prev) => !prev)}
                className="text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline"
              >
                {showAllHistorico
                  ? "Mostrar menos"
                  : `Exibir mais (${historico.length - HISTORICO_LIMIT})`}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
